import { useState, useEffect, useCallback } from "react";
import { useAuth, authFetch } from "../App";
import { toast } from "sonner";
import { format, addDays, subDays } from "date-fns";
import { de } from "date-fns/locale";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Car,
  Moon,
  Utensils,
  Save,
  ChevronDown,
  ChevronUp,
  FolderKanban,
  Calendar,
  AlertCircle,
  Plus,
  Trash2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";

const API = '/api';

// Default km to time factor (minutes per km)
const DEFAULT_KM_FACTOR = 1.2;

// Pause options
const PAUSE_OPTIONS = [
  { value: "0", label: "Keine Pause" },
  { value: "30", label: "30 Min" },
  { value: "60", label: "60 Min" },
];

// Verpflegung options
const VERPFLEGUNG_OPTIONS = [
  { value: 0, label: "0 €" },
  { value: 14, label: "14 €" },
  { value: 28, label: "28 €" },
];

// Override reasons for travel time
const OVERRIDE_REASONS = [
  { value: "stau", label: "Stau" },
  { value: "feierabendverkehr", label: "Feierabendverkehr" },
  { value: "umleitung", label: "Umleitung" },
  { value: "unfall", label: "Unfall" },
  { value: "witterung", label: "Witterung" },
  { value: "sonstiges", label: "Sonstiges" },
];

// Trip types
const TRIP_TYPES = [
  { value: "wohnort_projekt", label: "Wohnort \u2192 Projekt", direction: "hin" },
  { value: "projekt_unterkunft", label: "Projekt \u2192 Unterkunft", direction: "rueck" },
  { value: "unterkunft_projekt", label: "Unterkunft \u2192 Projekt", direction: "hin" },
  { value: "projekt_wohnort", label: "Projekt \u2192 Wohnort", direction: "rueck" },
];
const HIN_TYPES = ["wohnort_projekt", "unterkunft_projekt"];
const RUECK_TYPES = ["projekt_unterkunft", "projekt_wohnort"];

/**
 * Wiederverwendbarer Trip-Block fuer Hin-/Rueckfahrt.
 * Zeigt Liste der Fahrten in der jeweiligen Richtung + Add-Form.
 */
function TripBlock({
  title, icon: Icon, accentClass, bgClass, trips, allowedTypes, defaultType,
  expanded, onToggleExpanded, onDelete, onAdd, addingTrip, onStartAdd, onCancelAdd,
  newTrip, setNewTrip, onSuggestNextField, addTestId,
}) {
  const totalMinutes = trips.reduce((s, t) => s + (t.duration_minutes || 0), 0);
  return (
    <div className={`px-3 py-2 ${bgClass || ""}`}>
      <button onClick={onToggleExpanded} className="w-full flex items-center justify-between" data-testid={`toggle-${title.toLowerCase()}`}>
        <label className="text-[11px] font-medium text-gray-600 uppercase tracking-wider flex items-center gap-1.5">
          <span aria-hidden="true">🚗</span>
          <Icon className={`w-3 h-3 ${accentClass || ""}`} />
          {title}
          {trips.length > 0 && <span className="ml-1 text-[10px] font-mono text-gray-400 normal-case">({trips.length})</span>}
        </label>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {expanded && (
        <div className="mt-2 space-y-2">
          {/* Existing trips of this direction */}
          {trips.map(trip => (
            <div key={trip.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2" data-testid={`trip-row-${trip.id}`}>
              <div>
                <p className="text-xs font-medium text-gray-800">{trip.type_label}</p>
                <p className="text-[10px] text-gray-500">
                  {trip.start_time} - {trip.end_time} ({trip.duration_minutes} min)
                  {trip.km > 0 ? ` / ${trip.km} km` : ""}
                </p>
              </div>
              <button onClick={() => onDelete(trip.id)} className="p-1 hover:bg-red-50 rounded" data-testid={`trip-delete-${trip.id}`}>
                <Trash2 className="w-3.5 h-3.5 text-red-400" />
              </button>
            </div>
          ))}

          {/* Add trip form */}
          {addingTrip ? (
            <div className="bg-blue-50 rounded-lg p-3 space-y-2" data-testid={`add-trip-form-${title.toLowerCase()}`}>
              <Select value={newTrip.type} onValueChange={(v) => setNewTrip(p => ({ ...p, type: v }))}>
                <SelectTrigger className="h-9 text-[13px]"><SelectValue /></SelectTrigger>
                <SelectContent>{allowedTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] text-gray-500">Start</label>
                  <Input type="time" value={newTrip.start_time} onChange={e => setNewTrip(p => ({ ...p, start_time: e.target.value }))} className="h-8 text-[13px]" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500">Ende</label>
                  <Input type="time" value={newTrip.end_time} onChange={e => setNewTrip(p => ({ ...p, end_time: e.target.value }))} className="h-8 text-[13px]" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500">KM</label>
                  <Input type="number" min="0" placeholder="opt." value={newTrip.km} onChange={e => setNewTrip(p => ({ ...p, km: e.target.value }))} className="h-8 text-[13px]" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => onAdd(onSuggestNextField)} size="sm" className="flex-1 h-8 text-xs">Speichern</Button>
                <Button onClick={onCancelAdd} variant="outline" size="sm" className="h-8 text-xs">Abbrechen</Button>
              </div>
            </div>
          ) : (
            <button
              onClick={onStartAdd}
              className="w-full h-9 border border-dashed border-gray-300 rounded-lg text-xs text-gray-500 flex items-center justify-center gap-1 hover:border-gray-400 hover:text-gray-700 transition-colors bg-white/60"
              data-testid={addTestId}
            >
              <Plus className="w-3.5 h-3.5" /> {title} hinzufügen
            </button>
          )}

          {trips.length > 0 && (
            <div className="flex justify-between items-center pt-1 border-t border-gray-200">
              <span className="text-[10px] text-gray-500">Summe {title}</span>
              <span className="text-xs font-mono font-bold text-blue-600">{Math.floor(totalMinutes / 60)}h {totalMinutes % 60}m</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DailyOverview() {
  const { user } = useAuth();
  const isReducedView = user?.role === "nachunternehmer" || user?.extended_time_tracking === false;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [projects, setProjects] = useState([]);
  const [entry, setEntry] = useState(null);
  const [kmFactor, setKmFactor] = useState(DEFAULT_KM_FACTOR);
  
  // Expanded state for accordion (per direction)
  const [hinExpanded, setHinExpanded] = useState(true);
  const [rueckExpanded, setRueckExpanded] = useState(true);
  // Trips from trips collection
  const [trips, setTrips] = useState([]);
  // addingTrip: false | "hin" | "rueck"
  const [addingTrip, setAddingTrip] = useState(false);
  const [newTrip, setNewTrip] = useState({ type: "wohnort_projekt", start_time: "", end_time: "", km: "" });

  // Form state
  const [formData, setFormData] = useState({
    project_id: "",
    clock_in: "",
    clock_out: "",
    break_time: "0",
    // Hinfahrt
    outbound_km: "",
    outbound_time: "",
    outbound_override: false,
    outbound_override_reason: "",
    // Rückfahrt
    return_km: "",
    return_time: "",
    return_override: false,
    return_override_reason: "",
    // Übernachtung & Verpflegung
    overnight_stay: false,
    meal_allowance: 0,
    notes: "",
  });

  // Load projects
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const res = await authFetch(`${API}/projects`);
        if (res.ok) {
          const data = await res.json();
          setProjects(data.filter(p => p.is_active));
          
          // Load last used project from localStorage
          const lastProjectId = localStorage.getItem("lastProjectId");
          if (lastProjectId && data.some(p => p.id === lastProjectId)) {
            setFormData(prev => ({ ...prev, project_id: lastProjectId }));
          }
        }
      } catch (error) {
        console.error("Failed to load projects:", error);
      }
    };
    loadProjects();
  }, []);

  // Load km factor
  useEffect(() => {
    const loadKmFactor = async () => {
      try {
        const res = await authFetch(`${API}/settings/km-factor`);
        if (res.ok) {
          const data = await res.json();
          setKmFactor(data.km_factor || DEFAULT_KM_FACTOR);
        }
      } catch (error) {
        // Use default
      }
    };
    loadKmFactor();
  }, []);

  // Load entry for selected date
  const loadEntry = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const res = await authFetch(`${API}/time-entries/by-date/${dateStr}`);
      
      if (res.ok) {
        const data = await res.json();
        if (data && data.id) {
          setEntry(data);
          setFormData({
            project_id: data.project_id || "",
            clock_in: data.clock_in || "",
            clock_out: data.clock_out || "",
            break_time: String(data.break_time || "0"),
            outbound_km: String(data.outbound_km || ""),
            outbound_time: String(data.outbound_travel_minutes || ""),
            outbound_override: data.outbound_time_override || false,
            outbound_override_reason: data.outbound_override_reason || "",
            return_km: String(data.return_km || ""),
            return_time: String(data.return_travel_minutes || ""),
            return_override: data.return_time_override || false,
            return_override_reason: data.return_override_reason || "",
            overnight_stay: data.overnight_stay || false,
            meal_allowance: data.meal_allowance || 0,
            notes: data.notes || "",
          });
          // Expand travel if there's data (no-op: travelExpanded state was removed)
          if (data.outbound_km || data.return_km) {
            /* travel section auto-expanded elsewhere */
          }
        } else {
          setEntry(null);
          // Keep project_id from last entry
          const lastProjectId = localStorage.getItem("lastProjectId");
          setFormData(prev => ({
            ...prev,
            project_id: lastProjectId || prev.project_id,
            clock_in: "",
            clock_out: "",
            break_time: "0",
            outbound_km: "",
            outbound_time: "",
            outbound_override: false,
            outbound_override_reason: "",
            return_km: "",
            return_time: "",
            return_override: false,
            return_override_reason: "",
            overnight_stay: false,
            meal_allowance: 0,
            notes: "",
          }));
        }
      }
    } catch (error) {
      console.error("Failed to load entry:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    loadEntry();
  }, [loadEntry]);

  // Load trips for selected date
  const loadTrips = useCallback(async () => {
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const res = await authFetch(`${API}/trips?date=${dateStr}`);
      if (res.ok) { const d = await res.json(); setTrips(d.trips || []); }
    } catch {}
  }, [selectedDate]);

  useEffect(() => { if (user?.trip_entry_enabled) loadTrips(); }, [loadTrips, user?.trip_entry_enabled]);

  const handleAddTrip = async (suggestNextField) => {
    if (!newTrip.start_time || !newTrip.end_time) { toast.error("Start- und Endzeit eingeben"); return; }
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const res = await authFetch(`${API}/trips`, {
        method: "POST",
        body: JSON.stringify({ ...newTrip, date: dateStr, km: newTrip.km ? parseFloat(newTrip.km) : null }),
      });
      if (res.ok) {
        toast.success("Fahrt gespeichert");
        // Auto-Vorschlag (z.B. Hinfahrt-Ende -> Arbeitsbeginn)
        if (typeof suggestNextField === "function") {
          suggestNextField(newTrip.end_time);
        }
        loadTrips();
        setAddingTrip(false);
        setNewTrip({ type: "wohnort_projekt", start_time: "", end_time: "", km: "" });
      }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Fehler"); }
    } catch { toast.error("Fehler"); }
  };

  const handleDeleteTrip = async (id) => {
    try {
      const res = await authFetch(`${API}/trips/${id}`, { method: "DELETE" });
      if (res.ok) { toast.success("Geloescht"); loadTrips(); }
    } catch {}
  };

  // Auto-calculate travel time from km
  const calculateTravelTime = (km) => {
    if (!km || isNaN(parseFloat(km))) return "";
    return Math.round(parseFloat(km) * kmFactor);
  };

  // Handle outbound km change
  const handleOutboundKmChange = (value) => {
    const calculatedTime = calculateTravelTime(value);
    setFormData(prev => ({
      ...prev,
      outbound_km: value,
      outbound_time: prev.outbound_override ? prev.outbound_time : String(calculatedTime),
    }));
  };

  // Handle return km change
  const handleReturnKmChange = (value) => {
    const calculatedTime = calculateTravelTime(value);
    setFormData(prev => ({
      ...prev,
      return_km: value,
      return_time: prev.return_override ? prev.return_time : String(calculatedTime),
    }));
  };

  // Save entry
  const handleSave = async () => {
    if (!formData.project_id) {
      toast.error("Bitte wählen Sie ein Projekt");
      return;
    }

    setSaving(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      
      // Save last project to localStorage
      localStorage.setItem("lastProjectId", formData.project_id);
      
      const payload = {
        user_id: user.id,
        project_id: formData.project_id,
        date: dateStr,
        clock_in: formData.clock_in || null,
        clock_out: formData.clock_out || null,
        break_time: parseInt(formData.break_time) || 0,
        outbound_km: parseFloat(formData.outbound_km) || 0,
        outbound_travel_minutes: parseInt(formData.outbound_time) || 0,
        outbound_time_override: formData.outbound_override,
        outbound_override_reason: formData.outbound_override ? formData.outbound_override_reason : "",
        return_km: parseFloat(formData.return_km) || 0,
        return_travel_minutes: parseInt(formData.return_time) || 0,
        return_time_override: formData.return_override,
        return_override_reason: formData.return_override ? formData.return_override_reason : "",
        overnight_stay: formData.overnight_stay,
        meal_allowance: formData.meal_allowance,
        notes: formData.notes,
        // Route fields (defaults)
        route_start: "wohnort",
        route_end: "projekt",
        return_route_start: "projekt",
        return_route_end: "wohnort",
      };

      let res;
      if (entry?.id) {
        res = await authFetch(`${API}/time-entries/${entry.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        res = await authFetch(`${API}/time-entries`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }

      if (res.ok) {
        toast.success("Gespeichert");
        loadEntry();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Fehler beim Speichern");
      }
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  // Navigate dates
  const goToPreviousDay = () => setSelectedDate(prev => subDays(prev, 1));
  const goToNextDay = () => setSelectedDate(prev => addDays(prev, 1));
  const goToToday = () => setSelectedDate(new Date());

  // Check if today
  const isToday = format(selectedDate, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");

  return (
    <div className="pb-16" style={{ maxWidth: 640 }}>
      {/* Datum-Navigation – kompakt */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h2 className="text-[15px] font-semibold text-gray-800">Tagesübersicht</h2>
          <div className="flex items-center gap-1">
            <button onClick={goToPreviousDay} className="p-1 hover:bg-gray-100 rounded-sm"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={goToToday} className={`text-[13px] font-medium px-2 py-0.5 rounded-sm ${isToday ? "text-blue-600 bg-blue-50" : "text-gray-900"}`}>
              {format(selectedDate, "EEE, dd.MM.yyyy", { locale: de })}
            </button>
            <button onClick={goToNextDay} className="p-1 hover:bg-gray-100 rounded-sm"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200" style={{ borderRadius: 2 }}>
          {/* Formular – natuerliche Reihenfolge: Hinfahrt -> Arbeit -> Rueckfahrt */}
          <div className="divide-y divide-gray-100">
            {/* Projekt */}
            <div className="px-3 py-2">
              <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider block mb-1">Projekt *</label>
              <Select value={formData.project_id} onValueChange={(v) => setFormData({ ...formData, project_id: v })}>
                <SelectTrigger className="h-8 text-[13px]" data-testid="project-select"><SelectValue placeholder="Projekt" /></SelectTrigger>
                <SelectContent>{projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>

            {/* 1) HINFAHRT */}
            {!isReducedView && user?.trip_entry_enabled && (
              <TripBlock
                title="Hinfahrt"
                icon={Car}
                accentClass="text-blue-500"
                bgClass="bg-blue-50/40"
                trips={trips.filter(t => HIN_TYPES.includes(t.type))}
                allowedTypes={TRIP_TYPES.filter(t => t.direction === "hin")}
                defaultType="wohnort_projekt"
                expanded={hinExpanded}
                onToggleExpanded={() => setHinExpanded(v => !v)}
                onDelete={handleDeleteTrip}
                onAdd={handleAddTrip}
                addingTrip={addingTrip === "hin"}
                onStartAdd={() => { setAddingTrip("hin"); setNewTrip({ type: "wohnort_projekt", start_time: "", end_time: "", km: "" }); }}
                onCancelAdd={() => setAddingTrip(false)}
                newTrip={newTrip}
                setNewTrip={setNewTrip}
                onSuggestNextField={(endTime) => {
                  // Auto-Vorschlag: Arbeitsbeginn = Ende Hinfahrt
                  if (endTime && !formData.clock_in) {
                    setFormData(prev => ({ ...prev, clock_in: endTime }));
                  }
                }}
                addTestId="btn-add-hinfahrt"
              />
            )}

            {/* 2) ARBEITSZEIT */}
            <div className="px-3 py-2 bg-amber-50/30">
              <label className="text-[11px] font-medium text-gray-600 uppercase tracking-wider block mb-1 flex items-center gap-1.5">
                <span aria-hidden="true">🧰</span>
                Arbeitszeit
              </label>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] text-gray-400">Kommen</label>
                  <Input type="time" value={formData.clock_in} onChange={(e) => setFormData({ ...formData, clock_in: e.target.value })} className="h-8 text-[13px]" data-testid="clock-in" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400">Gehen</label>
                  <Input type="time" value={formData.clock_out} onChange={(e) => setFormData({ ...formData, clock_out: e.target.value })} className="h-8 text-[13px]" data-testid="clock-out" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400">Pause</label>
                  <Select value={formData.break_time} onValueChange={(v) => setFormData({ ...formData, break_time: v })}>
                    <SelectTrigger className="h-8 text-[13px]" data-testid="pause-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAUSE_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* 3) RUECKFAHRT */}
            {!isReducedView && user?.trip_entry_enabled && (
              <TripBlock
                title="Rückfahrt"
                icon={Car}
                accentClass="text-blue-500"
                bgClass="bg-blue-50/40"
                trips={trips.filter(t => RUECK_TYPES.includes(t.type))}
                allowedTypes={TRIP_TYPES.filter(t => t.direction === "rueck")}
                defaultType="projekt_wohnort"
                expanded={rueckExpanded}
                onToggleExpanded={() => setRueckExpanded(v => !v)}
                onDelete={handleDeleteTrip}
                onAdd={handleAddTrip}
                addingTrip={addingTrip === "rueck"}
                onStartAdd={() => {
                  // Auto-Vorschlag: Rueckfahrt-Start = Arbeitsende
                  setAddingTrip("rueck");
                  setNewTrip({ type: "projekt_wohnort", start_time: formData.clock_out || "", end_time: "", km: "" });
                }}
                onCancelAdd={() => setAddingTrip(false)}
                newTrip={newTrip}
                setNewTrip={setNewTrip}
                addTestId="btn-add-rueckfahrt"
              />
            )}

            {/* Uebernachtung + Verpflegung */}
            <div className="px-3 py-2 grid grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider block mb-1">Übernachtung</label>
                <div className="flex items-center gap-2">
                  <Switch checked={formData.overnight_stay} onCheckedChange={(v) => setFormData({ ...formData, overnight_stay: v })} data-testid="overnight-toggle" />
                  <span className="text-[12px] text-gray-600">{formData.overnight_stay ? "Ja" : "Nein"}</span>
                </div>
              </div>
              <div>
                <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider block mb-1">Verpflegung</label>
                <Select value={formData.meal_allowance} onValueChange={(v) => setFormData({ ...formData, meal_allowance: v })}>
                  <SelectTrigger className="h-8 text-[13px]" data-testid="meal-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{VERPFLEGUNG_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Notizen */}
            <div className="px-3 py-2">
              <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider block mb-1">Notizen</label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Besonderheiten…" rows={2} className="text-[13px] resize-none" data-testid="notes" />
            </div>
          </div>

          {/* Speichern – normal, nicht sticky hero */}
          <button
            onClick={handleSave}
            disabled={saving || !formData.project_id}
            className="mt-2 w-full h-9 bg-slate-800 hover:bg-slate-900 text-white text-[13px] font-medium rounded-sm disabled:opacity-40"
            data-testid="save-btn"
          >
            {saving ? "Speichern…" : "Speichern"}
          </button>
        </div>
      )}
    </div>
  );
}
